import * as turf from '@turf/turf';
import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { useMemo } from 'react';
import { useAppSelector } from '../../../../../app/hooks';
import { useBlock } from '../../../../../services/entities/hooks/useBlock';
import { Block } from '../../../../../services/entities/impl/blfApi/schemas/block';
import {
  UploadedBlockTypes,
  selectCurrOnboardBlockID,
} from '../../../../onboarding/onboardingSlice';
import { isValidGeoFeature } from '../../../../onboarding/onboardingUtils';
import { selectCurrBlockID } from '../../../../select/selectSlice';

export interface MeasureAcresResult {
  calculatedAcres: number | null;
  calculatedHectares: number | null;
  userInputAcres: number | null;
  userInputHectares: number | null;
  hasError: boolean;
  errorMessage: string | null;
  isLoading: boolean;
}
// Don't use division here to avoid zero division error
const acresToHectares = (acres: number) => acres * 0.4046856422;

/**
 * A function to validate and calculate acres based on the provided GeoJSON and block data.
 *
 * @param inputGeoJSON - An optional GeoJSON object to be validated.
 * @param block - The current block data.
 * @param isLoading - Loading state of the current block data.
 * @returns {MeasureAcresResult} - The calculated acres, error state, and any error messages.
 */
const validateAcres = (
  inputGeoJSON: UploadedBlockTypes | undefined,
  block: Block | undefined,
  isLoading: boolean
): MeasureAcresResult => {
  try {
    const targetGeoJSON =
      inputGeoJSON === undefined ? block?.geoPolygon : inputGeoJSON;

    if (!targetGeoJSON) {
      return {
        calculatedAcres: null,
        calculatedHectares: null,
        userInputAcres: block?.acres ?? null,
        userInputHectares: block?.acres ? acresToHectares(block.acres) : null,
        hasError: false,
        errorMessage: null,
        isLoading,
      };
    }

    let totalAcres = 0;

    if (targetGeoJSON.type === 'FeatureCollection') {
      for (const feature of (
        targetGeoJSON as FeatureCollection<Geometry, GeoJsonProperties>
      ).features) {
        if (isValidGeoFeature(feature, 'Polygon')) {
          const areaMetersSquared = turf.area(feature);
          const areaAcres = turf.convertArea(
            areaMetersSquared,
            'meters',
            'acres'
          );
          totalAcres += areaAcres;
        } else {
          return {
            calculatedAcres: null,
            calculatedHectares: null,
            userInputAcres: block?.acres ?? null,
            userInputHectares: block?.acres
              ? acresToHectares(block.acres)
              : null,
            hasError: true,
            errorMessage:
              'One or more features in FeatureCollection are invalid.',
            isLoading,
          };
        }
      }
    } else {
      if (isValidGeoFeature(targetGeoJSON, 'Polygon')) {
        const areaMetersSquared = turf.area(targetGeoJSON);
        totalAcres = turf.convertArea(areaMetersSquared, 'meters', 'acres');
      } else {
        return {
          calculatedAcres: null,
          calculatedHectares: null,
          userInputAcres: block?.acres ?? null,
          userInputHectares: block?.acres ? acresToHectares(block.acres) : null,
          hasError: true,
          errorMessage: 'Invalid GeoJSON.',
          isLoading,
        };
      }
    }

    return {
      calculatedAcres: totalAcres,
      calculatedHectares: totalAcres ? acresToHectares(totalAcres) : null,
      userInputAcres: block?.acres ?? null,
      userInputHectares: block?.acres ? acresToHectares(block.acres) : null,
      hasError: false,
      errorMessage: null,
      isLoading,
    };
  } catch (error) {
    console.error(error);
    return {
      calculatedAcres: null,
      calculatedHectares: null,
      userInputAcres: block?.acres ?? null,
      userInputHectares: block?.acres ? acresToHectares(block.acres) : null,
      hasError: true,
      errorMessage: 'Unknown error.',
      isLoading,
    };
  }
};

const useMeasureAcres = (
  inputGeoJSON?: UploadedBlockTypes,
  isOnboardMap = false
): MeasureAcresResult => {
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currOnboardBlockID = useAppSelector(selectCurrOnboardBlockID);

  const blockID = isOnboardMap ? currOnboardBlockID : currBlockID;

  const { block: blockData, isBlockLoading } = useBlock(blockID);

  const block = !blockID || isBlockLoading ? undefined : blockData;

  const measureResult = useMemo(
    () => validateAcres(inputGeoJSON, blockData, isBlockLoading),
    [block, inputGeoJSON, isBlockLoading]
  );

  return measureResult;
};

export default useMeasureAcres;
export { validateAcres };
